import { useRef, useCallback, useEffect, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronRight, X, ChevronUp, Filter, Eye } from "lucide-react";
import { useSearchStore } from "../../stores/searchStore";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../lib/utils";
import { getTypologyConfig, getTypologyLabel } from "../../lib/categoryConfig";
import type { OperatorWithCoords } from "../../types/database";

interface ResultsListProps {
  className?: string;
  onOperatorSelect?: (operator: OperatorWithCoords) => void;
  onClose?: () => void;
}

const ITEM_HEIGHT = 52; // Fixed height per item for consistent virtualization

export function ResultsList({ className, onOperatorSelect, onClose }: ResultsListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const {
    results,
    isSearching,
    setSelectedOperator,
    hoveredOperatorId,
    setHoveredOperatorId,
    listMode,
    setListMode,
    totalInView,
    filteredCount,
  } = useSearchStore();

  // Virtualizer configuration
  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 15, // Render extra items above/below viewport for smoother scrolling
  });

  const handleOperatorClick = useCallback((operator: OperatorWithCoords) => {
    setSelectedOperator(operator);
    onOperatorSelect?.(operator);
  }, [setSelectedOperator, onOperatorSelect]);

  const handleOperatorHover = useCallback((operatorId: string | null) => {
    setHoveredOperatorId(operatorId);
  }, [setHoveredOperatorId]);

  // Handle scroll to show/hide scroll-to-top button
  const handleScroll = useCallback(() => {
    if (parentRef.current) {
      setShowScrollTop(parentRef.current.scrollTop > 200);
    }
  }, []);

  // Scroll to top
  const scrollToTop = useCallback(() => {
    virtualizer.scrollToIndex(0, { align: "start", behavior: "smooth" });
  }, [virtualizer]);

  // Removed: scroll to selected operator (selection indication removed from list)

  // Scroll to hovered operator when hovering from map (only if not already visible)
  useEffect(() => {
    if (hoveredOperatorId && parentRef.current) {
      const index = results.findIndex(op => op.id === hoveredOperatorId);
      if (index !== -1) {
        // Check if item is already in view
        const virtualItems = virtualizer.getVirtualItems();
        const isInView = virtualItems.some(item => item.index === index);
        if (!isInView) {
          // Use "auto" for instant scroll (faster response when hovering on map)
          virtualizer.scrollToIndex(index, { align: "center", behavior: "auto" });
        }
      }
    }
  }, [hoveredOperatorId, results, virtualizer]);

  return (
    <div className={cn("flex flex-col h-full overlay-solid", className)}>
      {/* Header with toggle and close button */}
      <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Toggle Switch: Filtered / In View */}
          <div className="flex items-center bg-muted/60 rounded-md p-0.5 border border-border/50">
            <button
              onClick={() => setListMode("visible")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-semibold transition-all duration-150",
                listMode === "visible"
                  ? "bg-[#284899] text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
              )}
              title="Mostra solo operatori visibili sulla mappa"
            >
              <Eye className="h-3 w-3" />
              <span>Visibili</span>
              <span className={cn(
                "tabular-nums",
                listMode === "visible" ? "text-white/80" : "text-muted-foreground/70"
              )}>
                {isSearching ? "..." : totalInView.toLocaleString("it-IT")}
              </span>
            </button>
            <button
              onClick={() => setListMode("filtered")}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-semibold transition-all duration-150",
                listMode === "filtered"
                  ? "bg-[#284899] text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
              )}
              title="Mostra tutti gli operatori filtrati"
            >
              <Filter className="h-3 w-3" />
              <span>Filtrati</span>
              <span className={cn(
                "tabular-nums",
                listMode === "filtered" ? "text-white/80" : "text-muted-foreground/70"
              )}>
                {isSearching ? "..." : filteredCount.toLocaleString("it-IT")}
              </span>
            </button>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            title="Nascondi lista"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Virtualized Results - use relative/absolute for proper height */}
      <div className="flex-1 min-h-0 relative">
        {isSearching ? (
          <div className="flex items-center justify-center py-6">
            <Spinner size="sm" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-xs text-muted-foreground">
              Nessun risultato
            </p>
          </div>
        ) : (
          <>
            <div
              ref={parentRef}
              className="absolute inset-0 overflow-y-auto py-1"
              onScroll={handleScroll}
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const operator = results[virtualItem.index];
                  return (
                    <div
                      key={operator.id}
                      data-index={virtualItem.index}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      className="px-2 py-0.5"
                      onMouseEnter={() => handleOperatorHover(operator.id)}
                      onMouseLeave={() => handleOperatorHover(null)}
                    >
                      <ResultItem
                        operator={operator}
                        isSelected={false}
                        isHovered={hoveredOperatorId === operator.id}
                        onClick={() => handleOperatorClick(operator)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Scroll to top button */}
            {showScrollTop && (
              <button
                onClick={scrollToTop}
                className="absolute bottom-3 right-3 h-7 w-7 rounded-full overlay-solid-secondary border border-border text-muted-foreground shadow-md flex items-center justify-center hover:text-foreground hover:brightness-95 transition-colors z-10"
                title="Torna in cima"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface ResultItemProps {
  operator: OperatorWithCoords;
  isSelected: boolean;
  isHovered: boolean;
  onClick: () => void;
}

const ResultItem = ({ operator, isSelected, isHovered, onClick }: ResultItemProps) => {
  // Build location string: city · region (or just one if other is missing)
  const locationParts = [operator.city, operator.region].filter(Boolean);
  const locationString = locationParts.join(" · ");

  // Render stars for hotels
  const stars = operator.star_rating && operator.star_rating > 0
    ? Math.min(operator.star_rating, 5)
    : 0;

  // Get icon and color for typology (use parent macro category color)
  const typologyConfigData = getTypologyConfig(operator.typology, operator.macro_category);
  const TypologyIcon = typologyConfigData.icon;
  const typologyLabel = getTypologyLabel(operator.typology);

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full h-full text-left px-2.5 py-2 rounded-md transition-all duration-100",
        "hover:brightness-95 group",
        isHovered
          ? "overlay-solid-secondary ring-2 ring-[#284899] ring-inset"
          : "overlay-solid-secondary"
      )}
    >
      <div className="flex items-center gap-2">
        {/* Typology Icon */}
        <div
          className={cn(
            "flex items-center justify-center w-7 h-7 rounded-md shrink-0",
            isSelected ? "bg-white/20" : "bg-secondary"
          )}
        >
          <TypologyIcon
            className="h-3.5 w-3.5"
            style={{ color: isSelected ? "#ffffff" : typologyConfigData.color }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h4
              className={cn(
                "text-xs font-medium truncate",
                "text-foreground"
              )}
            >
              {operator.name}
            </h4>
            {stars > 0 && (
              <span
                className={cn(
                  "text-[10px] tracking-tight shrink-0",
                  "text-[#284899]"
                )}
              >
                {"★".repeat(stars)}
              </span>
            )}
          </div>
          <p
            className={cn(
              "text-[10px] truncate",
              "text-muted-foreground"
            )}
          >
            {typologyLabel || operator.typology}
            {locationString && ` · ${locationString}`}
          </p>
        </div>
        <ChevronRight
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-100",
            "group-hover:translate-x-0.5",
            "text-muted-foreground"
          )}
        />
      </div>
    </button>
  );
};
